import React, { Fragment, useEffect, useState } from "react";

import { camelCaseToSpacedPascalCase } from "@ds-developer/frontend";
import TestDetailsModal from "@ds-developer/test-details-modal";

import { PersonType, Test, TestType } from "@hubcapp/types";
import { lessonTypeToPersonType } from "@hubcapp/utils";
import { useAuth, useTealium } from "@hubcapp/context";

import { StateProviderProps } from "./types";
import TestDetails from "./TestDetails";
import useMutationCreateTest from "./hooks/useMutationCreateTest";
import useMutationDeleteTest from "./hooks/useMutationDeleteTest";
import useMutationUpdateTest from "./hooks/useMutationUpdateTest";
import useQueryTestDetails from "./hooks/useQueryTestDetails";

const TestDetailsStateProvider: React.FC<StateProviderProps> = ({
  learnerId,
}) => {
  const {
    instructor: { id: instructorId },
  } = useAuth();
  const { data, loading } = useQueryTestDetails(instructorId, learnerId);
  const { trackEvent } = useTealium();

  const [updateTestMutation] = useMutationUpdateTest(instructorId, learnerId);
  const [createTestMutation] = useMutationCreateTest(instructorId, learnerId);
  const [deleteTestMutation] = useMutationDeleteTest(instructorId, learnerId);

  const [modalOpen, setModalOpen] = useState(false);
  const [testForModal, setTestForModal] = useState<Test | undefined>();
  const [typesForModal, setTypesForModal] = useState<TestType[]>([]);

  useEffect(() => {
    const learnerType = lessonTypeToPersonType(
      data?.learner?.allocations[0]?.lessonType,
    );

    const learnerTestTypes = [TestType.Theory, TestType.Practical];
    const ditLearnerTestTypes = [TestType.Dit1, TestType.Dit2, TestType.Dit3];
    if (learnerType === PersonType.Dit) {
      setTypesForModal(ditLearnerTestTypes);
    } else {
      setTypesForModal(learnerTestTypes);
    }
  }, [data]);

  const closeModal = () => {
    trackEvent({
      eventCategory: `View Learner - ${
        testForModal ? "Edit A Test Date" : "Add A Test Date"
      }`,
      eventAction: "Button Click",
      eventLabel: "Cancel",
    });
    setModalOpen(false);
    setTestForModal(undefined);
  };

  const addClickHandler = () => {
    trackEvent({
      eventCategory: "View Learner",
      eventAction: "Button Click",
      eventLabel: "Add A Test Date",
    });
    setTestForModal(undefined);
    setModalOpen(true);
  };

  const editClickHandler = (test: Test) => {
    trackEvent({
      eventCategory: `View Learner - ${camelCaseToSpacedPascalCase(
        test.type,
      )} Test`,
      eventAction: "Button Click",
      eventLabel: "Edit",
    });

    setTestForModal(test);
    setModalOpen(true);
  };

  const handleModalOnSubmit = (test: Test) => {
    trackEvent({
      eventCategory: `View Learner - ${test.id ? "Edit" : "Add"} A Test Date`,
      eventAction: "Button Click",
      eventLabel: "Save",
    });

    const variables = { ...test, learnerId };

    if (test.id) {
      setModalOpen(false);
      setTestForModal(undefined);
      updateTestMutation({ variables });
    } else {
      setModalOpen(false);
      setTestForModal(undefined);
      createTestMutation({ variables });
    }
  };

  const handleModalOnDelete = (testId: string) => {
    trackEvent({
      eventCategory: "View Learner - Edit A Test Date",
      eventAction: "Button Click",
      eventLabel: "Delete",
    });

    setModalOpen(false);
    setTestForModal(undefined);

    deleteTestMutation({ variables: { id: testId } });
  };

  const trackChangeEvents = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    trackEvent({
      eventCategory: `View Learner - ${
        testForModal ? "Edit" : "Add"
      } A Test Date`,
      eventAction: "Interaction",
      eventLabel: `${name == "date" ? "Date" : event.target.value}`,
    });
  };

  return (
    <Fragment>
      <TestDetails
        tests={data?.learner.tests}
        addClickHandler={addClickHandler}
        editClickHandler={editClickHandler}
        loading={loading}
      />
      {modalOpen && (
        <TestDetailsModal
          onClose={closeModal}
          onSubmit={handleModalOnSubmit}
          onDelete={handleModalOnDelete}
          onChange={trackChangeEvents}
          types={typesForModal}
          test={testForModal}
        />
      )}
    </Fragment>
  );
};

export default TestDetailsStateProvider;
